import { useEffect, useState } from "react";
import { Copy, RefreshCw, MessageSquare, ExternalLink, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn, Loader, ErrorNote } from "./kit";
import { Modal } from "./Form";
import { Switch } from "../ui/switch";

export const reelUrl = (token) => `${window.location.origin}/p/${token}`;
export const refreshReel = (tid, pid) => web.post(`/teams/${tid}/players/${pid}/reel/refresh`).catch(() => {});

const Row = ({ label, hint, children }) => (
  <div className="flex items-center justify-between gap-4 rounded-xl border border-[#1E293B] bg-[#0F172A] px-4 py-3">
    <div><p className="text-sm font-semibold text-white">{label}</p>{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>
    {children}
  </div>
);

export const ReelShare = ({ open, onClose, tid, pid, name }) => {
  const [s, setS] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState(false);
  const path = `/teams/${tid}/players/${pid}/reel`;

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setS({ loading: true, error: null, data: null });
    web.get(path)
      .then((r) => { if (live) setS({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (live) setS({ loading: false, error: errMsg(e), data: null }); });
    return () => { live = false; };
  }, [open, path]);

  const save = async (body, msg) => {
    setBusy(true);
    try {
      const { data } = await web.post(path, body);
      setS({ loading: false, error: null, data });
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const d = s.data;
  const on = !!(d?.token && d.enabled);
  const url = d?.token ? reelUrl(d.token) : "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy. Select the link and copy it manually."); }
  };
  const sms = `sms:?&body=${encodeURIComponent(`${name} highlights & combine results: ${url}`)}`;

  return (
    <Modal open={open} onClose={onClose} testid="reel-share-dialog" title="Share highlights" description={`A public page with ${name}'s videos and combine results. Anyone with the link can view it; it's hidden from search engines.`}>
      {s.loading ? <Loader /> : s.error ? <ErrorNote error={s.error} /> : (
        <div className="mt-2 space-y-3">
          <Row label="Public page" hint={on ? "Anyone with the link can open it" : "Turned off — the link shows nothing"}>
            <Switch data-testid="reel-enabled-switch" checked={on} disabled={busy} onCheckedChange={(v) => save({ enabled: v }, v ? "Highlights page is on" : "Highlights page turned off")} />
          </Row>
          {d?.token && (
            <Row label="Show GPA" hint="Off by default">
              <Switch data-testid="reel-gpa-switch" checked={!!d.show_gpa} disabled={busy} onCheckedChange={(v) => save({ show_gpa: v }, v ? "GPA now shown" : "GPA hidden")} />
            </Row>
          )}
          {on && (
            <>
              <div className="flex items-center gap-2 rounded-xl border border-[#1E293B] bg-[#0F172A] p-1.5 pl-3.5">
                <input readOnly data-testid="reel-link-input" value={url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 bg-transparent font-mono-gd text-xs text-slate-200 outline-none" />
                <Btn data-testid="reel-copy-button" onClick={copy} className="px-4 py-2 text-xs"><Copy className="h-3.5 w-3.5" />Copy</Btn>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <a href={sms} data-testid="reel-text-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#39FF14]/50"><MessageSquare className="h-4 w-4" />Text the link</a>
                <a href={url} target="_blank" rel="noreferrer" data-testid="reel-open-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#39FF14]/50"><ExternalLink className="h-4 w-4" />Open page</a>
              </div>
              <p className="text-xs text-slate-400">
                Last updated {new Date(d.refreshed_at).toLocaleString()}. Changes made on the website update it automatically; for changes made in the mobile app, press Refresh.
              </p>
            </>
          )}
          <div className="flex flex-wrap justify-between gap-2 border-t border-[#1E293B] pt-4">
            <div className="flex gap-2">
              {d?.token && <Btn variant="ghost" disabled={busy} data-testid="reel-refresh-button" onClick={() => save({}, "Highlights page refreshed")} className="px-4 py-2 text-xs"><RefreshCw className="h-3.5 w-3.5" />Refresh</Btn>}
              {d?.token && <Btn variant="danger" disabled={busy} data-testid="reel-reset-button" className="px-4 py-2 text-xs"
                onClick={() => window.confirm("Make a new link? The old link stops working.") && save({ reset: true }, "New link created")}><KeyRound className="h-3.5 w-3.5" />New link</Btn>}
            </div>
            <Btn variant="ghost" onClick={onClose} data-testid="reel-close-button" className="px-4 py-2 text-xs">Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};
